import React, { useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import GridCard from 'employee-platform/shared/components/GridCard'
import FlexContainer from 'core-system/FlexContainer'
import palette from 'core-system/Themes/palette'
import { AppState } from 'redux/config/store'
import {
  EmployeeTransitPass,
  EmployeeTransitPassStatus,
  TransitAgency,
} from 'redux/transitPass/transitPassTypes'
import { transitPassActions } from 'redux/transitPass/transitPassActions'
import LinkTransitPassModal from './LinkTransitPassModal'
import RequestTransitPassModal from './RequestTransitPassModal'

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 1rem;
  min-height: 0;
`

const CardFooter = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const SavedValue = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const CardActions = styled(FlexContainer)`
  justify-content: flex-end;
  gap: 0.75rem;
`

const AgencyIcon = styled.img`
  width: 1.25rem;
  height: 1.25rem;
  object-fit: contain;
`

const StatusPill = styled.div<{ $background: string; $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background-color: ${(props) => props.$background};
  color: ${(props) => props.$color};
  font-size: 0.75rem;
  line-height: 1.1;
  font-weight: 600;
  white-space: nowrap;
`

const STATUS_PILL_STYLES: Record<
  EmployeeTransitPassStatus | 'UNLINKED',
  { label: string; background: string; color: string }
> = {
  UNLINKED: {
    label: 'No Card',
    background: palette.grey.grey100,
    color: palette.text.secondary,
  },
  REQUESTED: {
    label: 'Requested',
    background: palette.warning.yellow300,
    color: palette.warning.yellow800,
  },
  ORDERED: {
    label: 'Ordered',
    background: palette.warning.yellow300,
    color: palette.warning.yellow800,
  },
  VERIFYING: {
    label: 'Verifying',
    background: palette.accent.blue300,
    color: palette.accent.blue800,
  },
  ACTIVE: {
    label: 'Active',
    background: palette.success.green200,
    color: palette.success.green700,
  },
  DEACTIVATED: {
    label: 'Deactivated',
    background: palette.grey.grey200,
    color: palette.text.secondary,
  },
}

interface TransitAgencyCardProps {
  agency: TransitAgency
  employeeTransitPass?: EmployeeTransitPass | null
}

const TransitAgencyCard = React.memo(
  ({ agency, employeeTransitPass = null }: TransitAgencyCardProps) => {
    const dispatch = useDispatch()
    const { profileData } = useSelector(
      (state: AppState) => state.employeePlatform
    )
    const allowedDigitCounts = useSelector(
      (state: AppState) =>
        state.application.properties.transitPassCardsNumDigits ?? []
    )
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)

    const savedCardNumber = employeeTransitPass?.transitPassNumber ?? null
    const statusKey = employeeTransitPass?.status ?? 'UNLINKED'
    const statusPill = STATUS_PILL_STYLES[statusKey]
    const isRequested = employeeTransitPass?.status === 'REQUESTED'
    const isOrdered = employeeTransitPass?.status === 'ORDERED'
    const isActive = employeeTransitPass?.status === 'ACTIVE'
    const isVerifying = employeeTransitPass?.status === 'VERIFYING'
    const isDeactivated = employeeTransitPass?.status === 'DEACTIVATED'
    const requestDisabled = isRequested || isOrdered || isDeactivated
    const shippingAddress =
      profileData?.homeAddress || profileData?.worksiteAddress || null

    const handleLink = (cardNumber: string) => {
      dispatch(
        transitPassActions.createEmployeeTransitPass({
          transitAgency: agency.id,
          transitPassNumber: cardNumber,
          status: 'VERIFYING',
        })
      )
    }

    const handleRequest = () => {
      if (requestDisabled) {
        return
      }
      dispatch(
        transitPassActions.createEmployeeTransitPass({
          transitAgency: agency.id,
          transitPassNumber: null,
          status: 'REQUESTED',
        })
      )
      setIsRequestModalOpen(true)
    }

    return (
      <>
        <GridCard
          headerTitle={agency.name}
          headerIcon={
            agency.iconUrl ? (
              <AgencyIcon src={agency.iconUrl} alt={agency.name} />
            ) : (
              <CreditCard width={20} height={20} />
            )
          }
          customHeaderIcon={Boolean(agency.iconUrl)}
          headerButtons={
            <StatusPill
              $background={statusPill.background}
              $color={statusPill.color}
            >
              {statusPill.label}
            </StatusPill>
          }
        >
          <CardBody>
            {agency.description ? (
              <Text variant='body1' textColor={palette.text.secondary}>
                {agency.description}
              </Text>
            ) : null}
            <Text variant='body1' textColor={palette.text.secondary}>
              {agency.region
                ? `Enter an existing ${agency.name} card or request a new one (${agency.region}).`
                : `Enter an existing ${agency.name} card or request a new one.`}
            </Text>
            <CardFooter>
              {savedCardNumber ? (
                <SavedValue>
                  <Text variant='body1' textColor={palette.text.placeholder}>
                    Card Number
                  </Text>
                  <Text variant='action2'>{savedCardNumber}</Text>
                </SavedValue>
              ) : null}
              {isDeactivated ? (
                <Text variant='action4' textColor={palette.text.secondary}>
                  This card has been deactivated.
                </Text>
              ) : null}
              <CardActions>
                <Button
                  variant='secondary'
                  size='small'
                  disabled={isDeactivated}
                  onClick={() => setIsLinkModalOpen(true)}
                >
                  {(isActive || isVerifying || isOrdered) && savedCardNumber
                    ? 'Edit Card'
                    : 'Enter Card'}
                </Button>
                <Button
                  size='small'
                  disabled={requestDisabled}
                  onClick={handleRequest}
                >
                  {isRequested
                    ? 'Requested'
                    : isOrdered
                    ? 'Ordered'
                    : 'Request'}
                </Button>
              </CardActions>
            </CardFooter>
          </CardBody>
        </GridCard>
        <LinkTransitPassModal
          open={isLinkModalOpen}
          agency={agency}
          initialCardNumber={savedCardNumber}
          allowedDigitCounts={allowedDigitCounts}
          onClose={() => setIsLinkModalOpen(false)}
          onSave={handleLink}
        />
        <RequestTransitPassModal
          open={isRequestModalOpen}
          agency={agency}
          shippingAddress={shippingAddress}
          onClose={() => setIsRequestModalOpen(false)}
        />
      </>
    )
  }
)

TransitAgencyCard.displayName = 'TransitAgencyCard'

export default TransitAgencyCard
